import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar, Avatar, Badge, Box, Button, CircularProgress,
  Divider, IconButton, InputAdornment,
   Menu, MenuItem, Paper, Select, Snackbar, Alert, Stack,
  TextField, Toolbar, Typography, Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell,
  RadioGroup, Radio, FormControl, FormControlLabel, FormLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import dayjs, { Dayjs } from "dayjs";

import { useAuth } from "../auth/AuthContext";
import { useThemeMode } from "../ThemeModeProvider";
import { BRAND } from "../theme";
import { ProductVM, CategoryVM } from "../data/types";
import { toProductVM, toCategoryVM, toOrderVM } from "../data/mappers";
import { formatCAD, estimateHST, estimateTotal } from "../utils/currency";
import { FALLBACK_CATEGORIES } from "./categoryIcons";
import CategorySidebar from "./CategorySidebar";
import { buildCategoryTree, descendantNames, CatNode } from "./categoryTree";
import ProductImage from "../components/ProductImage";
import { foodImageDataUri } from "../utils/foodImage";
import ProductCard from "./ProductCard";
import ThemeColorPicker from "../components/ThemeColorPicker";

import { fetchProducts, fetchCategories, createOrder, fetchCustomerPrices, fetchOrders, createPayment, razorpayConfig, razorpayCreateOrder, razorpayVerify } from "../data/api";

function loadRazorpay(): Promise<boolean> {
  return new Promise((resolve) => {
    if ((window as any).Razorpay) return resolve(true);
    const sc = document.createElement("script");
    sc.src = "https://checkout.razorpay.com/v1/checkout.js";
    sc.onload = () => resolve(true);
    sc.onerror = () => resolve(false);
    document.body.appendChild(sc);
  });
}

const ALL = "All Products";

// B1: persist the buyer's cart across refresh / re-login.
const CART_NS = "fw_cart_v1_";
const cartKeyFor = (uid: number | null | undefined): string => `${CART_NS}${uid ?? "anon"}`;
type CartStore = { cart: Record<number, number>; unitMode: Record<number, "CASE" | "UNIT">; deliveryDate: string | null };
function readCartStore(key: string): CartStore {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { cart: parsed.cart || {}, unitMode: parsed.unitMode || {}, deliveryDate: parsed.deliveryDate ?? null };
    }
  } catch { /* ignore corrupt/blocked storage */ }
  return { cart: {}, unitMode: {}, deliveryDate: null };
}

const BuyerPortal: React.FC = () => {
  const navigate = useNavigate();
  const { username, displayName, userId, status, logout } = useAuth();
  const pendingApproval = status === "PENDING";
  useThemeMode(); // re-render this subtree when the theme colour changes
  const cartKey = cartKeyFor(userId);
  const [products, setProducts] = useState<ProductVM[]>([]);
  const [categories, setCategories] = useState<CategoryVM[]>([]);
  const [selected, setSelected] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [cart, setCart] = useState<Record<number, number>>(() => readCartStore(cartKeyFor(userId)).cart);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(() => { const d = readCartStore(cartKeyFor(userId)).deliveryDate; return d ? dayjs(d) : dayjs().add(3, "day"); });
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" | "info" }>({ open: false, msg: "", sev: "info" });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<{ id?: number; orderId?: string; total: number } | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("INVOICE");
  const [paying, setPaying] = useState(false);
  const [payCfg, setPayCfg] = useState<{ enabled: boolean; keyId: string; currency: string }>({ enabled: false, keyId: "", currency: "CAD" });
  const [priceOverrides, setPriceOverrides] = useState<Record<number, number>>({});
  const [unitMode, setUnitMode] = useState<Record<number, "CASE" | "UNIT">>(() => readCartStore(cartKeyFor(userId)).unitMode);
  const [orderFreq, setOrderFreq] = useState<Record<number, number>>({});
  const [showAllFrequent, setShowAllFrequent] = useState(false);

  const businessName = username || "My Business";

  const loadCatalogue = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [prodRaw, catRaw] = await Promise.all([fetchProducts(), fetchCategories()]);
      const allCats = (catRaw || []).map(toCategoryVM);
      const nameById = new Map<number, string>();
      allCats.forEach((c) => nameById.set(c.id, c.name));
      const prods = (prodRaw || []).map(toProductVM).map((pr) => ({
        ...pr,
        category: pr.category || (pr.categoryId != null ? nameById.get(pr.categoryId) : undefined),
      }));
      setProducts(prods);
      const roots = allCats.filter((c) => !c.parentId);
      setCategories(roots.length ? roots : FALLBACK_CATEGORIES.map((n, i) => ({ id: -(i + 1), name: n })));
    } catch {
      if (!silent) {
        setSnack({ open: true, msg: "Couldn't reach the catalogue. Is the backend reachable?", sev: "error" });
        setCategories(FALLBACK_CATEGORIES.map((n, i) => ({ id: -(i + 1), name: n })));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);
  useEffect(() => { razorpayConfig().then(setPayCfg); }, []);

  // Reflect product/price/image changes made elsewhere (e.g. distributor edits)
  // without a browser refresh: re-fetch silently when the tab regains focus.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") loadCatalogue(true); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadCatalogue]);

  useEffect(() => {
    if (userId == null) { setPriceOverrides({}); return; }
    (async () => { setPriceOverrides(await fetchCustomerPrices(userId)); })();
  }, [userId]);

  // Frequently-ordered is driven by the buyer's real order history (QA #8).
  useEffect(() => {
    (async () => {
      try {
        const orders = (await fetchOrders() || []).map(toOrderVM)
          .filter((o) => userId == null || o.userId == null || o.userId === userId);
        const freq: Record<number, number> = {};
        orders.forEach((o) => (o.lines || []).forEach((l) => {
          if (l.productId != null) freq[l.productId] = (freq[l.productId] || 0) + (l.quantity || 0);
        }));
        setOrderFreq(freq);
      } catch { setOrderFreq({}); }
    })();
  }, [userId]);

  // B1: load this buyer's saved cart once their id resolves (anon -> buyer),
  // then persist cart/unit/delivery-date on every change so a refresh keeps it.
  useEffect(() => {
    const st = readCartStore(cartKey);
    setCart(st.cart);
    setUnitMode(st.unitMode);
    if (st.deliveryDate) setDeliveryDate(dayjs(st.deliveryDate));
  }, [cartKey]);
  useEffect(() => {
    try {
      localStorage.setItem(cartKey, JSON.stringify({
        cart, unitMode,
        deliveryDate: deliveryDate ? deliveryDate.format("YYYY-MM-DD") : null,
      }));
    } catch { /* ignore blocked storage */ }
  }, [cart, unitMode, deliveryDate, cartKey]);

  // Products with the buyer's assigned (customer-specific) case price applied.
  const pricedProducts = useMemo(
    () => products.map((p) => ({ ...p, price: priceOverrides[p.id] ?? p.price })),
    [products, priceOverrides]
  );

  const priceFor = (p: ProductVM, unit: "CASE" | "UNIT") =>
    unit === "UNIT" && p.unitsPerCase && p.unitsPerCase > 0
      ? Math.round((p.price / p.unitsPerCase) * 100) / 100
      : p.price;

  const byId = useMemo(() => {
    const m = new Map<number, ProductVM>();
    pricedProducts.forEach((p) => m.set(p.id, p));
    return m;
  }, [pricedProducts]);

  // Hierarchical, de-duplicated category tree for the left sidebar.
  // Build the sidebar from the categories the products ACTUALLY carry (plus any
  // backend category names), so every menu click maps to real products.
  const tree = useMemo(() => {
    const names = [
      ...pricedProducts.map((p) => p.category || "").filter(Boolean),
      ...categories.map((c) => c.name),
    ];
    return names.length ? buildCategoryTree(names) : buildCategoryTree(FALLBACK_CATEGORIES);
  }, [pricedProducts, categories]);

  // name(lowercased) -> set of names covering that node and all its subcategories,
  // so selecting a parent filters in its children's products too.
  const coverageByName = useMemo(() => {
    const m = new Map<string, Set<string>>();
    const walk = (n: CatNode) => {
      m.set(n.name.trim().toLowerCase(),
        new Set(descendantNames(n).map((x) => x.trim().toLowerCase())));
      n.children.forEach(walk);
    };
    tree.forEach(walk);
    return m;
  }, [tree]);

  const filtered = useMemo(() => {
    let list = pricedProducts;
    if (selected !== ALL) {
      const cover = coverageByName.get(selected.trim().toLowerCase()) ?? new Set([selected.trim().toLowerCase()]);
      list = list.filter((p) => cover.has((p.category || "").trim().toLowerCase()));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      // B2: a category-name query expands to that category's whole subtree,
      // so typing "dairy" returns Dairy & Eggs products (and its children).
      const catCover = new Set<string>();
      coverageByName.forEach((descs, name) => {
        if (name.includes(q)) descs.forEach((d) => catCover.add(d));
      });
      list = list.filter((p) => {
        const cat = (p.category || "").trim().toLowerCase();
        return p.name.toLowerCase().includes(q) ||
          (p.sku || "").toLowerCase().includes(q) ||
          cat.includes(q) ||
          catCover.has(cat);
      });
    }
    if (sort === "priceLow") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "priceHigh") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [pricedProducts, selected, search, sort, coverageByName]);

  // Top items for the CURRENT menu selection, ranked by this buyer's order
  // history; falls back to the category's featured items when there's no history.
  const frequent = useMemo(() => {
    const inSel = (p: ProductVM) => {
      if (selected === ALL) return true;
      const cover = coverageByName.get(selected.trim().toLowerCase()) ?? new Set([selected.trim().toLowerCase()]);
      return cover.has((p.category || "").trim().toLowerCase());
    };
    const base = pricedProducts.filter(inSel);
    const ranked = [...base].sort((a, b) => (orderFreq[b.id] || 0) - (orderFreq[a.id] || 0));
    const ordered = ranked.filter((p) => (orderFreq[p.id] || 0) > 0);
    const list = ordered.length ? ordered : ranked;   // history first, else featured
    return list.slice(0, showAllFrequent ? 12 : 4);
  }, [pricedProducts, selected, coverageByName, orderFreq, showAllFrequent]);

  // QA #7: card selection is separate from the cart; this adds a chosen qty.
  // QA #6: never let the cart exceed available stock.
  const addQty = (id: number, q: number, unit: "CASE" | "UNIT" = "CASE") => {
    const product = byId.get(id);
    const stock = product?.stock ?? 0;
    const upc = product?.unitsPerCase && product.unitsPerCase > 0 ? product.unitsPerCase : 1;
    const max = unit === "UNIT" ? stock * upc : stock;      // stock is in cases
    const current = (unitMode[id] || "CASE") === unit ? (cart[id] || 0) : 0;
    const next = Math.min(max, current + q);
    if (next <= current) {
      setSnack({ open: true, msg: `Only ${max} available.`, sev: "info" });
      return;
    }
    if (current + q > max) {
      setSnack({ open: true, msg: `Only ${max} available — added up to the limit.`, sev: "info" });
    }
    setUnitMode((m) => ({ ...m, [id]: unit }));
    setCart((c) => ({ ...c, [id]: next }));
  };
  const inc = (id: number) => {
    const product = byId.get(id);
    const stock = product?.stock ?? 0;
    const unit = unitMode[id] || "CASE";
    const upc = product?.unitsPerCase && product.unitsPerCase > 0 ? product.unitsPerCase : 1;
    const max = unit === "UNIT" ? stock * upc : stock;
    setCart((c) => ({ ...c, [id]: Math.min(max || (c[id] || 0) + 1, (c[id] || 0) + 1) }));
  };
  const dec = (id: number) => setCart((c) => {
    const n = (c[id] || 0) - 1;
    const next = { ...c };
    if (n <= 0) delete next[id]; else next[id] = n;
    return next;
  });
  const removeLine = (id: number) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const lines = useMemo(
    () => Object.entries(cart)
      .map(([id, qty]) => {
        const product = byId.get(Number(id));
        if (!product) return null;
        const unit = (unitMode[Number(id)] || "CASE") as "CASE" | "UNIT";
        const unitPrice = priceFor(product, unit);
        return { product, qty, unit, unitPrice, lineTotal: unitPrice * qty };
      })
      .filter(Boolean) as { product: ProductVM; qty: number; unit: "CASE" | "UNIT"; unitPrice: number; lineTotal: number }[],
    [cart, byId, unitMode]
  );
  const itemCount = lines.reduce((s, l) => s + l.qty, 0);
  const subtotal = lines.reduce((s, l) => s + l.lineTotal, 0);
  const hst = estimateHST(subtotal);
  const total = estimateTotal(subtotal);

  const checkout = async () => {
    if (!lines.length) return;
    setPlacing(true);
    try {
      const created = await createOrder({
        userId: userId ?? undefined,
        customerName: displayName || username || undefined,
        orderDate: dayjs().toISOString(),
        status: "New",
        subtotal,
        tax: hst,
        totalAmount: total,
        ...(deliveryDate ? { deliveryDate: deliveryDate.format("YYYY-MM-DD") } : {}),
        items: lines.map(({ product, qty, unit, unitPrice }) => ({
          productId: product.id,
          productName: product.name,
          sku: product.sku,
          packagingUnit: unit === "UNIT" ? "Unit" : product.packagingUnit,
          quantity: qty,
          unitPrice,
          orderUnit: unit,
        })),
      });
      const num = created?.orderId ? ` ${created.orderId}` : "";
      setCreatedOrder({ id: created?.id, orderId: created?.orderId || num.trim(), total });
      setSnack({ open: true, msg: `Order${num} placed — ${itemCount} item(s), ${formatCAD(total)}.`, sev: "success" });
      setCart({});
      setUnitMode({});
      setReviewOpen(false);
      setCartOpen(false);
      setPayMethod("INVOICE");
      setPayOpen(true);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error
        || "Checkout failed. Please try again once the backend is reachable.";
      setSnack({ open: true, msg, sev: "error" });
    } finally {
      setPlacing(false);
    }
  };

  // The sidebar menu is driven by the categories products ACTUALLY have, so every
  // menu item filters to real results. We dedupe case-insensitively and sort A–Z.
  // Fallbacks: backend categories if products carry none, then the mockup list.

  const confirmPayment = async () => {
    if (!createdOrder) return;
    if (payMethod === "CARD") {
      setPaying(true);
      try {
        const ready = await loadRazorpay();
        if (!ready) throw new Error("Could not load the payment gateway.");
        const ord = await razorpayCreateOrder(createdOrder.id, createdOrder.total);
        const rzp = new (window as any).Razorpay({
          key: ord.keyId,
          amount: ord.amount,
          currency: ord.currency,
          name: "Food Wholesale",
          description: `Order ${createdOrder.orderId || ""}`,
          order_id: ord.razorpayOrderId,
          prefill: { email: username, name: displayName },
          theme: { color: BRAND.green },
          modal: { ondismiss: () => setPaying(false) },
          handler: async (resp: any) => {
            try {
              await razorpayVerify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                orderId: createdOrder.id,
                amount: createdOrder.total,
              });
              setSnack({ open: true, msg: `Payment successful — ${resp.razorpay_payment_id}.`, sev: "success" });
              setPayOpen(false);
            } catch {
              setSnack({ open: true, msg: "Payment verification failed. Please contact support.", sev: "error" });
            } finally {
              setPaying(false);
            }
          },
        });
        rzp.on("payment.failed", () => { setSnack({ open: true, msg: "Payment failed.", sev: "error" }); setPaying(false); });
        rzp.open();
      } catch (e: any) {
        setSnack({ open: true, msg: e?.message || "Could not start card payment.", sev: "error" });
        setPaying(false);
      }
      return;
    }
    setPaying(true);
    try {
      await createPayment({
        orderid: createdOrder.id,
        method: payMethod,
        status: "PENDING",                 // invoice/COD settle on fulfilment
        amount: createdOrder.total,
        totalamount: createdOrder.total,
        paymentdate: dayjs().format("YYYY-MM-DD"),
      });
      const label = payMethod === "COD" ? "Cash on delivery" : "Invoice (Net 30)";
      setSnack({ open: true, msg: `Payment method recorded: ${label}.`, sev: "success" });
      setPayOpen(false);
    } catch {
      setSnack({ open: true, msg: "Couldn't record the payment. The order is still placed.", sev: "error" });
    } finally {
      setPaying(false);
    }
  };

  const orderPanelContent = (
    <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Your Order</Typography>
            <Typography variant="caption" sx={{ bgcolor: BRAND.greenSoft, color: BRAND.green, px: 1, py: 0.25, borderRadius: 1, fontWeight: 700 }}>
              {itemCount} items
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 0.5 }}>
            {lines.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                Your order is empty. Add products to get started.
              </Typography>
            ) : lines.map(({ product, qty, unit, unitPrice }) => (
              <Stack key={product.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <Box sx={{ width: 44 }}><ProductImage src={product.image} fallback={foodImageDataUri(product.name, product.category)} alt={product.name} /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{product.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{unit === "UNIT" ? "Unit" : product.packagingUnit} · {formatCAD(unitPrice)}</Typography>
                </Box>
                <Stack direction="row" alignItems="center">
                  <IconButton size="small" onClick={() => dec(product.id)}><RemoveIcon fontSize="inherit" /></IconButton>
                  <Typography variant="body2" sx={{ minWidth: 18, textAlign: "center" }}>{qty}</Typography>
                  <IconButton size="small" onClick={() => inc(product.id)}><AddIcon fontSize="inherit" /></IconButton>
                </Stack>
                <IconButton size="small" onClick={() => removeLine(product.id)}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
              </Stack>
            ))}
          </Box>

          <Divider sx={{ my: 1 }} />
          <Stack spacing={0.5}>
            <Row label={`Subtotal (${itemCount} items)`} value={formatCAD(subtotal)} />
            <Row label="Estimated Tax (HST)" value={formatCAD(hst)} muted />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Estimated Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>CAD {formatCAD(total)}</Typography>
            </Stack>
          </Stack>

          <Button fullWidth variant="contained" size="large" sx={{ mt: 1.5 }} disabled={!lines.length || placing} onClick={() => setReviewOpen(true)}>
            {placing ? <CircularProgress size={22} color="inherit" /> : "Submit Order"}
          </Button>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Requested Delivery Date</Typography>
          <TextField
            type="date" size="small" fullWidth
            value={deliveryDate ? deliveryDate.format("YYYY-MM-DD") : ""}
            onChange={(e) => setDeliveryDate(e.target.value ? dayjs(e.target.value) : null)}
            inputProps={{ min: dayjs().add(1, "day").format("YYYY-MM-DD") }}
          />

          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: BRAND.greenSoft, border: "none" }}>
            <Stack direction="row" spacing={1}>
              <LocalShippingIcon fontSize="small" sx={{ color: BRAND.green }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Free delivery on orders over $500</Typography>
                <Typography variant="caption" color="text.secondary">Standard delivery within 1–2 business days.</Typography>
              </Box>
            </Stack>
          </Paper>
    </>
  );

  return (
    <Box sx={{ bgcolor: BRAND.surface, minHeight: "100vh" }}>
      {/* Top bar */}
      <AppBar position="sticky" elevation={0} color="inherit" sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "#fff" }}>
        <Toolbar sx={{ gap: 1, flexWrap: { xs: "wrap", md: "nowrap" }, py: { xs: 1, md: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ color: BRAND.green, flexShrink: 0 }}>
            <StorefrontIcon />
            <Typography variant="h6" sx={{ color: BRAND.green }}>Food Wholesale</Typography>
          </Stack>
          <TextField
            size="small" placeholder="Search products, brands or SKU..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ order: { xs: 3, md: 0 }, flexBasis: { xs: "100%", md: "auto" }, flex: { md: 1 }, maxWidth: { md: 620 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
          <Paper variant="outlined" sx={{ px: 1.5, py: 0.5, display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
            <StorefrontIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{businessName}</Typography>
              <Typography variant="caption" color="text.secondary">Customer #C1023</Typography>
            </Box>
          </Paper>
          <ThemeColorPicker />
          <IconButton onClick={() => setCartOpen(true)} aria-label="Open cart">
            <Badge badgeContent={itemCount} color="primary"><ShoppingCartIcon /></Badge>
          </IconButton>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: BRAND.green }}>
              {businessName.slice(0, 1).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => { setAnchorEl(null); navigate("/orders"); }}><ReceiptLongIcon fontSize="small" style={{ marginRight: 8 }} />My orders</MenuItem>
            <MenuItem onClick={logout}><LogoutIcon fontSize="small" style={{ marginRight: 8 }} />Sign out</MenuItem>
          </Menu>

          <Dialog open={reviewOpen} onClose={() => !placing && setReviewOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Review your order</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="body2"><b>Customer:</b> {displayName || username || "—"}</Typography>
                <Typography variant="body2"><b>Requested delivery:</b> {deliveryDate ? deliveryDate.format("MMM D, YYYY") : "Not set"}</Typography>
              </Stack>
              {lines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Your cart is empty.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Line total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map(({ product, qty, unit, unitPrice, lineTotal }) => (
                        <TableRow key={`${product.id}-${unit}`}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>{unit === "UNIT" ? "Unit" : (product.packagingUnit || "Case")}</TableCell>
                          <TableCell align="right">{qty}</TableCell>
                          <TableCell align="right">{formatCAD(unitPrice)}</TableCell>
                          <TableCell align="right">{formatCAD(lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={0.5} sx={{ ml: "auto", maxWidth: 260 }}>
                <Row label={`Subtotal (${itemCount} items)`} value={formatCAD(subtotal)} />
                <Row label="Estimated Tax (HST)" value={formatCAD(hst)} muted />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total (CAD)</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatCAD(total)}</Typography>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Final prices, taxes and stock are confirmed by the server when you place the order.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReviewOpen(false)} disabled={placing}>Keep shopping</Button>
              <Button variant="contained" onClick={checkout} disabled={placing || !lines.length || pendingApproval}>
                {placing ? <CircularProgress size={20} color="inherit" /> : "Place order"}
              </Button>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>

      {pendingApproval && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Your account is awaiting distributor approval. You can browse the catalogue,
          but checkout is disabled until a distributor approves your account.
        </Alert>
      )}

      {/* Body: sidebar | main | order panel */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px 1fr", lg: "220px 1fr 340px" }, gap: 2, p: 2, alignItems: "start" }}>
        {/* Category sidebar (hierarchical) */}
        <Paper variant="outlined" sx={{ p: 1, position: { lg: "sticky" }, top: 80, maxHeight: "calc(100vh - 96px)", overflowY: "auto" }}>
          <CategorySidebar tree={tree} selected={selected} allLabel={ALL} onSelect={setSelected} />
        </Paper>

        {/* Main column */}
        <Box sx={{ minWidth: 0 }}>
          {/* Hero */}
          <Paper variant="outlined" sx={{ position: "relative", overflow: "hidden", mb: 2, minHeight: 170,
            background: `linear-gradient(90deg, ${BRAND.greenDark}, ${BRAND.green})`, color: "#fff", display: "flex", alignItems: "center", p: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>Fresh Produce</Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>Direct to Your Business</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Quality products. Competitive wholesale pricing.</Typography>
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Frequently ordered */}
              {frequent.length > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">
                      {selected === ALL ? "Your Frequently Ordered Items" : `Top in ${selected}`}
                    </Typography>
                    <Button size="small" onClick={() => setShowAllFrequent((v) => !v)}>
                      {showAllFrequent ? "Show less" : "View all"}
                    </Button>
                  </Stack>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 3 }}>
                    {frequent.map((p) => (
                      <ProductCard key={`f-${p.id}`} product={p} qty={cart[p.id] || 0} compact onAdd={addQty} />
                    ))}
                  </Box>
                </>
              )}

              {/* All products */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">{selected === ALL ? "All Products" : selected}</Typography>
                <Select size="small" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="featured">Sort by: Featured</MenuItem>
                  <MenuItem value="priceLow">Price: Low to High</MenuItem>
                  <MenuItem value="priceHigh">Price: High to Low</MenuItem>
                  <MenuItem value="name">Name: A to Z</MenuItem>
                </Select>
              </Stack>

              {filtered.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                  No products{search ? ` match "${search}"` : " in this category"} yet.
                </Paper>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }, gap: 1.5 }}>
                  {filtered.map((p) => (
                    <ProductCard key={p.id} product={p} qty={cart[p.id] || 0} onAdd={addQty} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Order panel (sticky sidebar, large screens) */}
        <Paper variant="outlined" sx={{ p: 2, position: { lg: "sticky" }, top: 80, display: { xs: "none", lg: "block" } }}>
          {orderPanelContent}
        </Paper>
      </Box>

      <Dialog open={payOpen} onClose={() => !paying && setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Payment{createdOrder?.orderId ? ` — ${createdOrder.orderId}` : ""}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Amount due: <b>{createdOrder ? formatCAD(createdOrder.total) : "—"}</b>
          </Typography>
          <FormControl>
            <FormLabel>Payment method</FormLabel>
            <RadioGroup value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <FormControlLabel value="INVOICE" control={<Radio />} label="Invoice — Net 30 (wholesale terms)" />
              <FormControlLabel value="COD" control={<Radio />} label="Cash on delivery" />
              <FormControlLabel value="CARD" disabled={!payCfg.enabled} control={<Radio />}
                label={payCfg.enabled ? "Online payment — card / UPI (Razorpay)" : "Online card payment (gateway not configured)"} />
            </RadioGroup>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Your order is already placed; choose how you'll pay. Online card payments require a
            configured Razorpay gateway (test keys) — online card/UPI is live only when it is set.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)} disabled={paying}>Skip for now</Button>
          <Button variant="contained" onClick={confirmPayment} disabled={paying || (payMethod === "CARD" && !payCfg.enabled)}>
            {paying ? <CircularProgress size={20} color="inherit" /> : "Confirm payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={cartOpen} onClose={() => setCartOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, p: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Your Cart</Typography>
          <Button size="small" onClick={() => setCartOpen(false)}>Close</Button>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        {orderPanelContent}
      </Drawer>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string; muted?: boolean }> = ({ label, value, muted }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color={muted ? "text.secondary" : "text.primary"}>{label}</Typography>
    <Typography variant="body2" color={muted ? "text.secondary" : "text.primary"}>{value}</Typography>
  </Stack>
);

export default BuyerPortal;
